<template>
    <nav class="tab-nav-container" :class="{ 'nav-top': navTop }">
        <button v-if="navTop && logo" @click="scrollToTop" class="logo-container">
            <LocationLogo :logo="logo" :name="logoName" />
        </button>
        <button v-if="showLeftArrow" class="arrow" @click="scrollLeft">
            <SvgIcon name="forward" class="arrow-icon left" />
        </button>
        <ul ref="tabsList" class="tabs-list">
            <li v-for="tab in tabs" :key="tab.ref"
                :class="{ 'is-selected': isSelected(tab), 'has-unmet': hasUnmet(tab) }" @click="selectTab(tab)">
                <SvgIcon name="reorder" class="reorder-icon animate__animated animate__rotateIn"
                    v-if="tab.name == 'Order Again'" />
                {{ tab.name }}
            </li>
        </ul>
        <button v-if="showRightArrow" class="arrow" @click="scrollRight">
            <SvgIcon name="forward" class="arrow-icon right" />
        </button>
    </nav>
</template>

<script>
import debounce from 'lodash/debounce';
import SvgIcon from './SvgIcon.vue';
import LocationLogo from './LocationLogo.vue'; // import the LocationLogo component

export default {
    name: "TabNav",
    components: { SvgIcon, LocationLogo },
    props: {
        tabs: Array,
        selectedTabRef: String,
        navTop: Boolean,
        logo: String,
        logoName: String,
        unmetRequirements: Array, // New prop to receive unmet requirements
    },
    data() {
        return {
            showLeftArrow: false,
            showRightArrow: false,
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.checkScrollArrows();
        });
        this.debouncedCheck = debounce(this.checkScrollArrows, 200);
        window.addEventListener('resize', this.debouncedCheck);
    },
    unmounted() {
        window.removeEventListener('resize', this.debouncedCheck);
    },
    methods: {
        selectTab(tab) {
            this.$emit('tab-selected', tab.ref);
        },
        isSelected(tab) {
            return this.selectedTabRef === tab.ref;
        },
        hasUnmet(tab) {
            return this.unmetRequirements?.includes(tab.ref);
        },
        checkScrollArrows() {
            const el = this.$refs.tabsList;
            if (!el) return;

            this.showLeftArrow = el.scrollLeft > 0;
            this.showRightArrow = el.scrollWidth > el.clientWidth + el.scrollLeft;
        },
        scrollLeft() {
            this.scrollTabs(-1);
        },
        scrollRight() {
            this.scrollTabs(1);
        },
        scrollTabs(direction) {
            const el = this.$refs.tabsList;
            el.scrollBy({ left: direction * el.clientWidth / 2, behavior: 'smooth' });
            this.checkScrollArrows();
        },
        scrollToTop() {
            this.selectTab(this.tabs[0]);
        }
    }
};
</script>

<style scoped>
.tab-nav-container {
    display: flex;
    align-items: center;
    padding: 0 2.5rem;
    z-index: 5000;
    background-color: var(--grey-50);
}

.nav-top.tab-nav {
    position: sticky;
    top: -0.0625rem;
    border-bottom: 0.125rem solid var(--grey-outline);
    box-shadow: var(--box-shadow);
    padding-top: 1rem !important;
    padding-bottom: 0.875rem !important;
    background-color: var(--primary-white);
    padding-left: 1rem;
}

.tabs-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    overflow-x: auto;
    flex-grow: 1;
    white-space: nowrap;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.tabs-list::-webkit-scrollbar {
    display: none;
}

li {
    cursor: pointer;
    margin: 0.3125rem;
    font-size: 1.625rem;
    padding: 0.3125rem 0.625rem;
    color: var(--primary-black);
    font-weight: normal;
    position: relative;
}

li:first-child {
    margin-left: 0;
    padding-left: 0.625rem;
}

li.is-selected {
    transform: scale(1.05);
    transition: transform 0.3s ease;
    font-weight: bold;
    border-bottom: 0.3125rem solid var(--primary-color);
}

li.has-unmet::after {
    content: '*';
    color: red;
    font-size: 1rem;
    position: absolute;
    top: 0;
    right: 0;
}

.arrow {
    display: none;
    border: none;
    cursor: pointer;
    width: 6rem;
}

.arrow-icon {
    fill: var(--primary-white);
    color: var(--primary-white);
    background-color: var(--primary-color);
    width: 1.5rem;
    height: 1.5rem;
    display: inline-block;
    border-radius: 50%;
    border: 0.125rem solid var(--grey-outline);
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
    transform: rotate(0deg);
    padding: 0.25rem;
}

.arrow-icon.left {
    transform: rotate(180deg);
}

.tab-nav-container .logo-container {
    height: 3rem;
    /* Adjust based on the height of a tab */
    width: 3rem;
    margin-right: 1rem;
}
</style>
